import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface SupportTicket {
  id: string;
  userId: string;
  subject: string;
  category: 'PAYMENT' | 'TECHNICAL' | 'ACCOUNT' | 'KYC' | 'PAYOUT' | 'REFUND' | 'ABUSE' | 'OTHER';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  assignedToId?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
  };
  assignedTo?: {
    id: string;
    username: string;
    email: string;
  };
  messages?: TicketMessage[];
  _count?: {
    messages: number;
  };
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  senderId: string;
  message: string;
  isInternal: boolean;
  createdAt: string;
  sender?: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
    isSupport?: boolean;
    isAdmin?: boolean;
  };
}

export interface SupportStats {
  openTickets: number;
  inProgressTickets: number;
  waitingUserTickets: number;
  resolvedToday: number;
  totalTickets: number;
  urgentTickets: number;
  escalatedTickets: number;
  unassignedTickets: number;
  avgResolutionTimeHours: number;
}

export interface SupportAgentStatus {
  id: string;
  userId: string;
  status: 'ONLINE' | 'BUSY' | 'AWAY' | 'OFFLINE';
  currentLoad: number;
  maxConcurrent: number;
  lastActiveAt: string;
  totalChats: number;
  avgRating?: number;
  availableFrom?: string;
  availableUntil?: string;
  user?: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
  };
  stats?: {
    assignedTickets: number;
    resolvedTickets: number;
    resolvedLast30Days: number;
    avgResolutionTime: number | null;
  };
}

export interface SlaMetrics {
  windowDays: number;
  totals: {
    tickets: number;
    breaches: number;
    avgFirstResponseHours: number;
  };
  byPriority: {
    URGENT: { open: number; breaches: number; breachRate: number };
    HIGH: { open: number; breaches: number; breachRate: number };
    MEDIUM: { open: number; breaches: number; breachRate: number };
    LOW: { open: number; breaches: number; breachRate: number };
  };
  targetsHours: {
    URGENT: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
}

export const supportService = {
  async createTicket(data: {
    subject: string;
    description: string;
    category?: string;
    priority?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/support/tickets`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportTicket;
  },

  async getTickets(params: { 
    status?: string; 
    priority?: string; 
    category?: string; 
    assigneeId?: string | null;
    search?: string;
    page?: number; 
    limit?: number;
    sortBy?: 'createdAt' | 'updatedAt' | 'priority';
    sortOrder?: 'asc' | 'desc';
  } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/tickets`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { tickets: SupportTicket[]; pagination: any };
  },

  async getTicket(ticketId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/tickets/${ticketId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportTicket;
  },

  async addMessage(ticketId: string, data: { message: string; isInternal?: boolean }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/support/tickets/${ticketId}/messages`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as TicketMessage;
  },

  async assignTicket(ticketId: string, assigneeId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/support/tickets/${ticketId}/assign`, { assigneeId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportTicket;
  },

  async autoAssignTicket(ticketId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/support/tickets/${ticketId}/auto-assign`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportTicket;
  },

  async updateStatus(ticketId: string, status: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/support/tickets/${ticketId}/status`, { status }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportTicket;
  },

  async escalateTicket(ticketId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/support/tickets/${ticketId}/escalate`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportTicket;
  },

  async bulkUpdateTickets(ticketIds: string[], updates: {
    status?: string;
    priority?: string;
    assigneeId?: string;
    isEscalated?: boolean;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/support/tickets/bulk-update`, { ticketIds, updates }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { success: boolean; updatedCount: number };
  },

  async searchTickets(query: string, params: { page?: number; limit?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/tickets/search`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { q: query, ...params },
    });
    return res.data as { tickets: SupportTicket[]; pagination: any };
  },

  async getStats() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportStats;
  },

  async getAgentStatus() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/agent-status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportAgentStatus;
  },

  async updateAgentStatus(data: {
    status?: 'ONLINE' | 'BUSY' | 'AWAY' | 'OFFLINE';
    maxConcurrent?: number;
    availableFrom?: string;
    availableUntil?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/support/agent-status`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportAgentStatus;
  },

  async getAvailableAgents() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/available-agents`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportAgentStatus[];
  },

  async getAgentStats(userId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/agent-stats/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as SupportAgentStatus;
  },

  async getSlaMetrics(windowDays?: number) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/support/sla-metrics`, {
      headers: { Authorization: `Bearer ${token}` },
      params: windowDays ? { windowDays } : {},
    });
    return res.data as SlaMetrics;
  },
};
